import {} from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { EmployeePicker, EquipmentPicker, PanelContent } from 'components';
import EquipmentInspectionChecklistPicker from 'components/domain/equipment/EquipmentInspectionChecklistPicker/EquipmentInspectionChecklistPicker';
import EquipmentInspectionConditionPicker from 'components/domain/equipment/EquipmentInspectionConditionPicker/EquipmentInspectionConditionPicker';
import EquipmentInspectionTypePicker from 'components/domain/equipment/EquipmentInspectionTypePicker/EquipmentInspectionTypePicker';
import SidePanelFilter from 'components/foundation/FilterSidePanel/FilterSidePanel';
import BasicNonNullPicker from 'components/foundation/pickers/basic-picker/BasicNonNullPicker/BasicNonNullPicker';
import SidePanelTimeRangeSection from 'components/foundation/SidePanelTimeRangeSection/SidePanelTimeRangeSection';
import { useQueryParams, useTimeRange } from 'hooks';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import useTimeRangeTimeRangeTypeQueryParams from 'hooks/routing/useTimeRangeTimeRangeTypeQueryParams/useTimeRangeTimeRangeTypeQueryParams';
import { t } from 'i18next';
import { DateTime, Duration } from 'luxon';
import { useMemo, useState } from 'react';
import { ClassName } from 'types/ClassName';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { QueryParam } from 'utils/constants/queryParam';
import { anyFilterIsSet, filterHasChanged } from 'utils/filterUtils';
import { EquipmentInspectionsTabType } from '../EquipmentInspectionsReportTabHeader/EquipmentInspectionsReportTabHeader';

export interface IEquipmentInspectionsFilterProps {
  className?: ClassName;
}

export enum DueWithin {
  THIRTY_DAYS = '30',
  FIFTEEN_DAYS = '15',
  SEVEN_DAYS = '7',
  OVERDUE = 'overdue',
}

export interface IEquipmentInspectionsFilterData {
  timeRangeType: TimeRangeType | null;
  customTimeRange: ITimeRange<DateTime | null>;
  dueWithin: DueWithin | null;
  equipmentId: string | null;
  inspectionType: string | null;
  checklistId: string | null;
  inspectorId: string | null;
  condition: string | null;
}

const EquipmentInspectionsFilter = (props: IEquipmentInspectionsFilterProps) => {
  const { className } = props;

  const memberSettings = useMemberSettings();
  const { getParam, updateParams } = useQueryParams();
  const parseTimeRangeTimeRangeType = useTimeRangeTimeRangeTypeQueryParams();
  const { timeRangeType, timeRange } = parseTimeRangeTimeRangeType(
    TimeRangeType.MONTHLY,
    memberSettings?.web?.features?.equipmentInspectionsReport?.equipmentInspectionsCompletedReport
      ?.lastUsedTimeRangeType,
    'utc'
  );
  const currentTab =
    (getParam(QueryParam.EQUIPMENT_INSPECTION_REPORT_TYPE) as EquipmentInspectionsTabType) ??
    memberSettings?.web?.features?.equipmentInspectionsReport?.lastUsedTab;

  const currentInspectionType = getParam(QueryParam.EQUIPMENT_INSPECTION_TYPE);
  const currentEquipmentId = getParam(QueryParam.EQUIPMENT_ID);
  const currentChecklistId = getParam(QueryParam.EQUIPMENT_INSPECTION_CHECKLIST_ID);
  const currentStartDate = getParam(QueryParam.START_DATE);
  const currentEndDate = getParam(QueryParam.END_DATE);
  const currentInspectorId = getParam(QueryParam.EQUIPMENT_INSPECTOR_ID);
  const currentCondition = getParam(QueryParam.EQUIPMENT_CONDITION);
  const currentDueWithin = getParam(QueryParam.INSPECTION_DUE_WITHIN);

  const [data, setData] = useState<IEquipmentInspectionsFilterData>({
    timeRangeType,
    customTimeRange: {
      startTime: currentStartDate
        ? DateTime.fromFormat(currentStartDate, 'yyyy-MM-dd')
        : (timeRange?.startTime ?? null),
      endTime: currentEndDate ? DateTime.fromFormat(currentEndDate, 'yyyy-MM-dd') : (timeRange?.endTime ?? null),
    },
    dueWithin: currentDueWithin as DueWithin,
    equipmentId: currentEquipmentId,
    inspectionType: currentInspectionType,
    checklistId: currentChecklistId,
    inspectorId: currentInspectorId,
    condition: currentCondition,
  });

  const payPeriodRange = useTimeRange(TimeRangeType.PAY_PERIOD);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentPayPeriodEndTime = useMemo<DateTime>(() => payPeriodRange.timeRange.endTime, []);

  const handleApply = () => {
    const hasCustomTimeRangePicked = data.timeRangeType === TimeRangeType.CUSTOM;

    const timeRangeChanged =
      data.timeRangeType !== timeRangeType ||
      (hasCustomTimeRangePicked &&
        (data.customTimeRange.startTime?.toISODate() !== currentStartDate ||
          data.customTimeRange.endTime?.toISODate() !== currentEndDate));

    const params = {
      [QueryParam.TIME_RANGE_TYPE]: data.timeRangeType ?? null,
      [QueryParam.EQUIPMENT_ID]: data.equipmentId ?? null,
      [QueryParam.EQUIPMENT_INSPECTION_TYPE]: data.inspectionType ?? null,
      [QueryParam.EQUIPMENT_INSPECTION_CHECKLIST_ID]: data.checklistId ?? null,
      [QueryParam.EQUIPMENT_INSPECTOR_ID]: data.inspectorId ?? null,
      [QueryParam.EQUIPMENT_CONDITION]: data.condition ?? null,
      [QueryParam.INSPECTION_DUE_WITHIN]: data.dueWithin ?? null,
      ...(hasCustomTimeRangePicked
        ? {
            [QueryParam.START_DATE]: data.customTimeRange.startTime?.toISODate(),
            [QueryParam.END_DATE]: data.customTimeRange.endTime?.toISODate(),
          }
        : timeRangeChanged && {
            [QueryParam.START_DATE]: null,
            [QueryParam.END_DATE]: null,
          }),
    };

    updateParams(params);
  };

  const handleClear = () => {
    setData({
      ...data,
      equipmentId: null,
      inspectionType: null,
      checklistId: null,
      inspectorId: null,
      condition: null,
      dueWithin: null,
    });
    const params = {
      [QueryParam.EQUIPMENT_ID]: null,
      [QueryParam.EQUIPMENT_INSPECTION_TYPE]: null,
      [QueryParam.EQUIPMENT_INSPECTION_CHECKLIST_ID]: null,
      [QueryParam.EQUIPMENT_INSPECTOR_ID]: null,
      [QueryParam.EQUIPMENT_CONDITION]: null,
      [QueryParam.INSPECTION_RESOLVED_STATUS]: null,
    };
    updateParams(params);
  };

  const hasChanged = () => {
    const current = {
      timeRangeType,
      dueWithin: currentDueWithin as DueWithin,
      equipmentId: currentEquipmentId,
      inspectionType: currentInspectionType,
      checklistId: currentChecklistId,
      inspectorId: currentInspectorId,
      condition: currentCondition,
    };
    return (
      filterHasChanged(current, data) ||
      (data.timeRangeType === TimeRangeType.CUSTOM
        ? currentStartDate !== data.customTimeRange.startTime?.toISODate() ||
          currentEndDate !== data.customTimeRange.endTime?.toISODate()
        : false)
    );
  };

  const anyFilterSet = () => {
    const ignoredProperties: (keyof IEquipmentInspectionsFilterData)[] = [
      'timeRangeType',
      'customTimeRange',
      'dueWithin',
    ];
    return anyFilterIsSet<IEquipmentInspectionsFilterData>(data, ignoredProperties);
  };

  const onSelectField =
    <K extends keyof IEquipmentInspectionsFilterData, V extends IEquipmentInspectionsFilterData[K]>(
      key: keyof IEquipmentInspectionsFilterData
    ) =>
    (value: V) => {
      setData({ ...data, [key]: value });
    };

  const getDisabledDateRange = () => {
    const clampDuration = Duration.fromObject({ days: 425 });
    return {
      disabledAfter: currentPayPeriodEndTime.plus(clampDuration),
      disabledBefore: data.customTimeRange.endTime
        ? data.customTimeRange.endTime.minus(clampDuration)
        : currentPayPeriodEndTime.minus(clampDuration),
    };
  };

  const dueWithinOptions = [
    { name: t('Due Within 30 Days'), id: DueWithin.THIRTY_DAYS },
    { name: t('Due Within 15 Days'), id: DueWithin.FIFTEEN_DAYS },
    { name: t('Due Within 7 Days'), id: DueWithin.SEVEN_DAYS },
    { name: t('Overdue'), id: DueWithin.OVERDUE },
  ];

  const classes = classNames('equipment-inspections-filter p-5', className);

  return (
    <SidePanelFilter
      onApply={handleApply}
      onClear={handleClear}
      appliable={hasChanged()}
      clearable={anyFilterSet()}
      className={classes}
    >
      <PanelContent flex={false}>
        <>
          <h3 className="pb-5">{t('Settings')}</h3>
          {currentTab === EquipmentInspectionsTabType.COMPLETED ? (
            <SidePanelTimeRangeSection
              timeRangeType={data.timeRangeType ?? TimeRangeType.MONTHLY}
              includeAllTime={false}
              onTimeRangeTypeChange={onSelectField('timeRangeType')}
              onCustomTimeRangeChange={onSelectField('customTimeRange')}
              customTimeRange={data.customTimeRange}
              {...getDisabledDateRange()}
            />
          ) : (
            <BasicNonNullPicker
              value={data.dueWithin ?? DueWithin.THIRTY_DAYS}
              onSelect={onSelectField('dueWithin')}
              data={dueWithinOptions}
            />
          )}
          <div className="py-5">
            <h3 className="pb-5">{t('Filter')}</h3>
            <EquipmentPicker
              value={data.equipmentId}
              placeholder={t('Equipment')}
              onSelect={onSelectField('equipmentId')}
              className="mb-4"
            />
            {currentTab === EquipmentInspectionsTabType.COMPLETED && (
              <EquipmentInspectionTypePicker
                value={data.inspectionType}
                onSelect={onSelectField('inspectionType')}
                className="mb-4"
              />
            )}
            <EquipmentInspectionChecklistPicker
              value={data.checklistId}
              onSelect={onSelectField('checklistId')}
              className="mb-4"
            />
            <EmployeePicker
              value={data.inspectorId}
              placeholder={t('Inspector')}
              onSelect={onSelectField('inspectorId')}
              className="mb-4"
            />
            {currentTab === EquipmentInspectionsTabType.COMPLETED && (
              <EquipmentInspectionConditionPicker
                value={data.condition}
                onSelect={onSelectField('condition')}
                className="mb-4"
              />
            )}
          </div>
        </>
      </PanelContent>
    </SidePanelFilter>
  );
};

export default EquipmentInspectionsFilter;
